import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { ChangeEvent, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLoading } from '@/providers/LoadingProvider';
import { useRolesObject } from '@/hooks/api/0290_roles';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { SimpleTextFormat } from '@/components/element/SimpleTextFormat';
import { useCurrentTeam } from '@/providers/TeamProvider';
import { useUpdateTeamMember } from '@/hooks/api/0310_update_team_member';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toastSuccess } from '@/lib/toast';
import { useTeamInvalidate } from '@/hooks/api/0210_team';
import { Role } from '@/hooks/api/models';

type FormInputs = {
  identifier: string;
  team_id: number;
  team_member_id: number;
  'role_ids[]': string[];
  default_backofficer: boolean;
  default_approver: boolean;
};

export const TeamMemberEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('チームメンバー権限編集'), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `チーム設定`, path: `/w/${_w_!.identifier}/setting/teams` },
        { label: `チーム詳細`, path: `/w/${_w_!.identifier}/setting/teams/${_t_!.id}` },
      ]),
    []
  );

  const navigate = useNavigate();
  const { team_member_id } = useParams();
  const { setShowLoading } = useLoading();
  const { _w_, me, _t_, isAdmin } = useCurrentTeam();
  const { adminRole, approverRole, financialOfficerRole, backofficeRole, memberRole } = useRolesObject();
  const { mutate, data, isPending, error } = useUpdateTeamMember();
  const { invalidate } = useTeamInvalidate({ identifier: _w_!.identifier, id: _t_!.id });
  const teamMember = _t_!.team_members.find((m) => m.id == Number(team_member_id));
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      team_member_id: teamMember?.id,
      'role_ids[]': teamMember ? teamMember.roles.map((r) => String(r.id)) : [],
      default_approver: teamMember?.default_approver,
      default_backofficer: teamMember?.default_backofficer,
    },
  });

  useEffect(() => {
    if (teamMember == null) {
      navigate(`/w/${_w_!.identifier}/setting/teams/${_t_!.id}`);
    }
  }, []);

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      toastSuccess('チームメンバー権限を編集しました');
      invalidate();
      navigate(-1);
    }
  }, [data]);

  const onChangeDefault = (e: ChangeEvent<HTMLInputElement>, role: Role) => {
    if (e.target.checked) {
      const roleIds = getValues('role_ids[]');
      if (roleIds.indexOf(String(role.id)) == -1) {
        roleIds.push(String(role.id));
        setValue('role_ids[]', roleIds);
      }
    }
  };

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'team'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">チームメンバー権限編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="col-md-12">
                    <div className="form-group">
                      <label>チーム名</label>
                      <p className="form-text text-muted text-sm ms-1">{_t_!.name}</p>
                    </div>

                    <div className="form-group">
                      <label>ユーザー</label>
                      <p className="form-text text-muted text-sm ms-1">{teamMember!.user.user_account.name}</p>
                    </div>

                    <div className="form-group">
                      <label>
                        権限<span className="text-danger">*</span>
                      </label>

                      {adminRole && (
                        <>
                          <div className="form-group">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="r-admin"
                                value={adminRole.id}
                                {...register('role_ids[]')}
                              />
                              <label className="form-check-label" htmlFor="r-admin">
                                チーム管理者
                              </label>
                            </div>
                          </div>
                          <div className="mb-3 text-xs">
                            <SimpleTextFormat content={adminRole.description} />
                          </div>
                          <hr className="horizontal dark" />
                        </>
                      )}

                      {approverRole && (
                        <>
                          <div className="form-group row">
                            <div className="col-6 col-md-6 col-lg-5">
                              <div className="form-check form-switch me-5">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="r-approver"
                                  value={approverRole.id}
                                  {...register('role_ids[]')}
                                  onChange={(e) => {
                                    if (!e.target.checked) setValue('default_approver', false);
                                  }}
                                />
                                <label className="form-check-label" htmlFor="r-approver">
                                  承認者
                                </label>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="default-approver"
                                  {...register('default_approver')}
                                  onChange={(e) => onChangeDefault(e, approverRole)}
                                />
                                <label className="custom-control-label" htmlFor="default-approver">
                                  メイン承認者にする
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="mb-3 text-xs">
                            <SimpleTextFormat content={approverRole.description} />
                          </div>
                          <hr className="horizontal dark" />
                        </>
                      )}

                      {financialOfficerRole && (
                        <>
                          <div className="form-group">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="r-financial"
                                value={financialOfficerRole.id}
                                {...register('role_ids[]')}
                              />
                              <label className="form-check-label" htmlFor="r-financial">
                                経理担当者
                              </label>
                            </div>
                          </div>
                          <div className="mb-3 text-xs">
                            <SimpleTextFormat content={financialOfficerRole.description} />
                          </div>
                          <hr className="horizontal dark" />
                        </>
                      )}

                      {backofficeRole && (
                        <>
                          <div className="form-group row">
                            <div className="col-6 col-md-6 col-lg-5">
                              <div className="form-check form-switch me-5">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="r-bo"
                                  value={backofficeRole.id}
                                  {...register('role_ids[]')}
                                  onChange={(e) => {
                                    if (!e.target.checked) setValue('default_backofficer', false);
                                  }}
                                />
                                <label className="form-check-label" htmlFor="r-bo">
                                  バックオフィス担当者
                                </label>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="default-backofficer"
                                  {...register('default_backofficer')}
                                  onChange={(e) => onChangeDefault(e, backofficeRole)}
                                />
                                <label className="custom-control-label" htmlFor="default-backofficer">
                                  メインバックオフィス担当にする
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="mb-3 text-xs">
                            <SimpleTextFormat content={backofficeRole.description} />
                          </div>
                          <hr className="horizontal dark" />
                        </>
                      )}

                      {memberRole && (
                        <>
                          <div className="form-group">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="r-member"
                                value={memberRole.id}
                                {...register('role_ids[]')}
                              />
                              <label className="form-check-label" htmlFor="r-member">
                                メンバー
                              </label>
                            </div>
                          </div>
                          <div className="mb-3 text-xs">
                            <SimpleTextFormat content={memberRole.description} />
                          </div>
                          <hr className="horizontal dark" />
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link
                    to={`/w/${_w_!.identifier}/setting/teams/${_t_!.id}`}
                    className="btn btn-outline-secondary me-2"
                  >
                    戻る
                  </Link>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
